import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { fmtCurrency, fmtDate, statusLabel, PHASE_TEMPLATES } from '../lib/utils';
import { Card, Badge, ProgressBar, Avatar, Btn, Modal, Field, Input, Select, Textarea, PageHeader, Empty, Spinner, Toast, B } from '../components/UI';
import { useAuth } from '../context/AuthContext';

const STATUSES = ['all', 'scheduled', 'in-progress', 'final-inspection', 'completed', 'on-hold'];

export default function JobsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const query = supabase.from('jobs').select('*').order('created_at', { ascending: false });
    const { data } = await query;
    setJobs(data || []);
    const { data: custs } = await supabase.from('profiles').select('id,full_name,email').eq('role', 'customer');
    setCustomers(custs || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = jobs.filter(j => {
    const ms = filter === 'all' || j.status === filter;
    const q = search.toLowerCase();
    const mq = !q || (j.customer_name || '').toLowerCase().includes(q) || (j.address || '').toLowerCase().includes(q) || (j.type || '').toLowerCase().includes(q) || (j.id || '').toLowerCase().includes(q);
    return ms && mq;
  });

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader
        title={isAdmin ? 'All Jobs' : 'My Projects'}
        action={isAdmin && <Btn variant="primary" onClick={() => setShowNew(true)}>+ New Job</Btn>}
      />

      {/* Search & filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Input placeholder="🔍  Search jobs, customers, addresses…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {STATUSES.map(s => {
            const active = filter === s;
            return (
              <button key={s} onClick={() => setFilter(s)} style={{ fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 20, border: 'none', background: active ? (s === 'all' ? B.navy : '#E2E8F0') : '#F1F5F9', color: active ? (s === 'all' ? '#fff' : B.navy) : '#64748B', cursor: 'pointer', fontFamily: "'Barlow',sans-serif" }}>
                {s === 'all' ? 'All' : statusLabel(s)}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 && <Empty icon="🏠" title="No jobs found" sub="Try a different search or filter." />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.map(j => {
          const matCost = 0; // computed server-side in prod
          return (
            <Card key={j.id} onClick={() => navigate(`/jobs/${j.id}`)}>
              <div style={{ display: 'flex', gap: 12 }}>
                <Avatar name={j.customer_name} size={44} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, color: '#94A3B8', fontSize: 13 }}>{j.id?.slice(0, 12)}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: B.navy }}>{j.customer_name}</span>
                    <Badge status={j.status} />
                  </div>
                  <div style={{ fontSize: 13, color: '#475569', marginBottom: 3 }}>📍 {j.address}</div>
                  <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>{j.type}{j.material ? ` · ${j.material}` : ''}</div>
                  <ProgressBar value={j.progress || 0} />
                  <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#64748B' }}>📅 {fmtDate(j.start_date)}</span>
                    <span style={{ fontSize: 12, color: '#64748B' }}>🎯 {fmtDate(j.est_complete)}</span>
                    {isAdmin && <span style={{ fontSize: 12, color: '#64748B' }}>💰 {fmtCurrency(j.contract_total)}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Barlow Condensed',sans-serif", color: B.navy }}>{j.progress || 0}%</div>
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>complete</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* New Job Modal */}
      {showNew && (
        <NewJobModal
          customers={customers}
          onClose={() => setShowNew(false)}
          onCreate={job => { setJobs(prev => [job, ...prev]); setShowNew(false); setToast({ msg: `Job created!`, type: 'success' }); navigate(`/jobs/${job.id}`); }}
          onToast={t => setToast(t)}
        />
      )}
      {toast && <Toast message={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

function NewJobModal({ customers, onClose, onCreate, onToast }) {
  const [form, setForm] = useState({ customer_id: '', address: '', type: 'Full Replacement', material: '', start_date: '', est_complete: '', contract_total: '', sq_footage: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  async function submit() {
    if (!form.customer_id || !form.address) { onToast({ msg: 'Customer and address required.', type: 'error' }); return; }
    setSaving(true);
    const customer = customers.find(c => c.id === form.customer_id);
    const phases = (PHASE_TEMPLATES[form.type] || PHASE_TEMPLATES['Full Replacement']).map((label, i) => ({ label, done: false, current: i === 0, date: '', completed_date: '', notes: '' }));
    const { data, error } = await supabase.from('jobs').insert({
      customer_id: form.customer_id,
      customer_name: customer?.full_name || '',
      address: form.address, type: form.type, material: form.material,
      status: 'scheduled', progress: 0,
      start_date: form.start_date || null,
      est_complete: form.est_complete || null,
      contract_total: parseFloat(form.contract_total) || 0,
      amount_paid: 0, sq_footage: parseFloat(form.sq_footage) || 0,
      notes: form.notes, phases,
    }).select().single();
    setSaving(false);
    if (error) { onToast({ msg: 'Error creating job: ' + error.message, type: 'error' }); return; }
    onCreate(data);
  }

  return (
    <Modal title="Create New Job" onClose={onClose} wide
      footer={<><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn variant="primary" onClick={submit} disabled={saving}>{saving ? 'Creating…' : 'Create Job'}</Btn></>}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
        <Field label="Customer" required span2><Select value={form.customer_id} onChange={f('customer_id')}><option value="">— Select customer —</option>{customers.map(c => <option key={c.id} value={c.id}>{c.full_name} ({c.email})</option>)}</Select></Field>
        <Field label="Property Address" required span2><Input value={form.address} onChange={f('address')} placeholder="123 Main St, Indianapolis, IN" /></Field>
        <Field label="Job Type" required><Select value={form.type} onChange={f('type')}>{Object.keys(PHASE_TEMPLATES).map(t => <option key={t}>{t}</option>)}</Select></Field>
        <Field label="Material"><Input value={form.material} onChange={f('material')} placeholder="e.g. GAF Timberline HDZ" /></Field>
        <Field label="Start Date"><Input type="date" value={form.start_date} onChange={f('start_date')} /></Field>
        <Field label="Est. Completion"><Input type="date" value={form.est_complete} onChange={f('est_complete')} /></Field>
        <Field label="Contract Value ($)"><Input type="number" value={form.contract_total} onChange={f('contract_total')} placeholder="0" /></Field>
        <Field label="Sq. Footage"><Input type="number" value={form.sq_footage} onChange={f('sq_footage')} placeholder="0" /></Field>
        <Field label="Notes" span2><Textarea value={form.notes} onChange={f('notes')} placeholder="HOA requirements, insurance details, special instructions…" /></Field>
      </div>
    </Modal>
  );
}
